import { SettlementUnitPicker } from './SettlementUnitPicker';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Button, IconButton } from '@/components/Button';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { parsePeriod, periodParam, thisMonth, useGet, useStlPerms, useUrlState, type Period, type StatementAction } from './api';
import { statementStatus } from './status';
import type { StatementListItem, StatementStatus } from './types';
import { PdfLink, StatementCard, statementCols } from './StatementList';
import { StatementActionDialog } from './StatementActionDialog';
import { StatementBatchExport } from './StatementBatchExport';
import { Chips, FilterButton, FilterSheet, PeriodBar, SearchBox, StlHeader, Table, type ChipItem, type Col } from './ui';
import { ViewSidebar, type SidebarView } from '@/components/ViewSidebar';
import s from './StatementsPage.module.css';

const PAGE = 30;
const STATUSES: StatementStatus[] = ['DRAFT', 'CONFIRMED', 'LOCKED', 'CANCELLED'];

export function StatementsPage() {
  const perms = useStlPerms();
  return perms.followerView ? <FollowerStatementsPage /> : <RegularStatementsPage />;
}

/** 跟单员只看本人相关客户的对账单状态。V2 接口不含金额、PDF 或详情。 */
function FollowerStatementsPage() {
  const ff = useFormFactor();
  const [sp, set] = useUrlState();
  const period = parsePeriod(sp.get('period'), 'all');
  const month = period.kind === 'month' ? period.value : undefined;
  const year = period.kind === 'year' ? period.value : undefined;
  const q = (sp.get('q') || '').trim();
  const active = (STATUSES as string[]).includes(sp.get('st') || '') ? sp.get('st')! : 'all';
  const list = useGet<StatementListItem[]>('/api/v2/settlements/follower-statements', { month, year });
  const data = list.fresh ? list.data : undefined;
  const searched = (data || []).filter((x) => !q || `${x.statementNo} ${x.partner?.name || ''}`.toLowerCase().includes(q.toLowerCase()));
  const shown = active === 'all' ? searched : searched.filter((x) => x.status === active);
  const chips: ChipItem[] = [
    { key: 'all', label: '全部', count: searched.length },
    ...STATUSES.map((status) => ({ key: status, label: statementStatus[status].label, count: searched.filter((x) => x.status === status).length })),
  ];
  const setPeriod = (next: Period) => set({ period: periodParam(next, 'all') });
  const header = <StlHeader phone={ff === 'phone'} active="statement" counts={data ? { statement: data.length } : undefined} />;
  const toolbar = <div className={s.followerToolbar}>
    <Chips items={chips} value={active} onChange={(st) => set({ st: st === 'all' ? undefined : st })} />
    <PeriodBar period={period} onPeriod={setPeriod} />
    <SearchBox key={q} value={q} placeholder="对账单号 / 客户" onSubmit={(value) => set({ q: value || undefined })} />
  </div>;
  const body = list.error && !data ? <ErrorState title="对账单加载失败" message={list.error.message} onRetry={list.reload} />
    : !data ? <div className={s.pad}><Skeleton /><Skeleton lines={3} /></div>
      : !shown.length ? <Empty icon="receipt" title="没有符合条件的对账单">换个月份或清空搜索条件再看。</Empty> : null;
  const columns: Col<StatementListItem>[] = [
    { key: 'no', title: '对账单号', w: 190, render: (x) => x.statementNo },
    { key: 'customer', title: '客户', w: 220, render: (x) => x.partner?.name || '—' },
    { key: 'month', title: '结算月份', w: 130, render: (x) => x.settlementMonth },
    { key: 'status', title: '状态', w: 110, render: (x) => statementStatus[x.status].label },
    { key: 'lines', title: '业务单据', w: 110, render: (x) => `${x.lineCount} 张` },
  ];
  return <div className={ff === 'phone' ? s.phone : s.page}>
    {header}
    {toolbar}
    <p className={s.note}>仅显示与你跟单的客户相关的对账单状态；金额与 PDF 不在此视角展示。</p>
    <div className={s.followerBody}>{body || (ff === 'phone' ? shown.map((x) => <StatementCard key={x.id} x={x} />) : <Table cols={columns} rows={shown} rowKey={(x) => x.id} />)}</div>
  </div>;
}

/** 对账单列表（高保真稿 T7）：状态 chips 按流转顺序排，已作废单独放在后面 */
function RegularStatementsPage() {
  const ff = useFormFactor();
  const perms = useStlPerms();
  const nav = useNavigate();
  const [sp, set] = useUrlState();
  const st = (STATUSES as string[]).includes(sp.get('st') || '') ? (sp.get('st') as StatementStatus) : 'all';
  const scopeParam = sp.get('scope');
  const scope = scopeParam === 'customer' || scopeParam === 'supplier' ? scopeParam : undefined;
  // 对账单默认看全部月份；月份写进 URL 的 period（与往来页同一格式）
  const period = parsePeriod(sp.get('period'), 'all');
  const month = period.kind === 'month' ? period.value : undefined;
  const q = sp.get('q') || '';
  const page = Math.max(1, Number(sp.get('page')) || 1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [actionTarget, setActionTarget] = useState<{ item: StatementListItem; action: StatementAction } | null>(null);
  const [createPicker, setCreatePicker] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const [sortNewest, setSortNewest] = useState(true);
  const both = perms.customer && perms.supplier;

  const includeCustomer = scope === 'customer' || (!scope && perms.customer);
  const includeSupplier = scope === 'supplier' || (!scope && perms.supplier);
  const customerList = useGet<StatementListItem[]>(perms.statement && includeCustomer ? '/api/settlements/statements' : null, { list: 1, scope: 'customer', month });
  const supplierList = useGet<StatementListItem[]>(perms.statement && includeSupplier ? '/api/settlements/statements' : null, { list: 1, scope: 'supplier', month });
  const listReady = (!includeCustomer || (customerList.fresh && customerList.data !== undefined)) && (!includeSupplier || (supplierList.fresh && supplierList.data !== undefined));
  const combined = useMemo(() => {
    if (!listReady) return undefined;
    return [
      ...(includeCustomer ? customerList.data || [] : []),
      ...(includeSupplier ? supplierList.data || [] : []),
    ].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt) || a.statementNo.localeCompare(b.statementNo));
  }, [listReady, includeCustomer, includeSupplier, customerList.data, supplierList.data]);
  const list = {
    data: combined,
    loading: (includeCustomer && customerList.loading) || (includeSupplier && supplierList.loading),
    error: (includeCustomer ? customerList.error : undefined) || (includeSupplier ? supplierList.error : undefined),
    url: [includeCustomer ? customerList.url : undefined, includeSupplier ? supplierList.url : undefined].filter(Boolean).join(' + '),
    reload: () => { if (includeCustomer) customerList.reload(); if (includeSupplier) supplierList.reload(); },
  };
  const all = useMemo(() => {
    const kw = q.toLowerCase();
    return (list.data ?? []).filter((x) => !kw || x.statementNo.toLowerCase().includes(kw) || x.partner?.name.toLowerCase().includes(kw));
  }, [list.data, q]);
  const shown = st === 'all' ? all : all.filter((x) => x.status === st);
  const ordered = useMemo(() => [...shown].sort((a, b) => {
    const aTime = Date.parse(a.updatedAt) || 0;
    const bTime = Date.parse(b.updatedAt) || 0;
    const recentFirst = bTime - aTime || a.statementNo.localeCompare(b.statementNo);
    return sortNewest ? recentFirst : -recentFirst;
  }), [shown, sortNewest]);

  if (!perms.statement) {
    return (
      <div className={s.page}>
        <Empty icon="lock" title="没有查看对账单的权限" action={<Link to="/settlements" className={s.linkBtn}>回结算总览</Link>}>
          需要「查看对账单」权限，请联系管理员在老版的角色设置里开通。
        </Empty>
      </div>
    );
  }

  const setPeriod = (p: Period) => set({ period: periodParam(p, 'all'), id: undefined, page: undefined });
  const count = (k: StatementStatus) => all.filter((x) => x.status === k).length;
  const chipItems: ChipItem[] = [
    { key: 'all', label: '全部', count: list.data ? all.length : undefined },
    { key: 'DRAFT', label: '草稿', count: list.data ? count('DRAFT') : undefined },
    { key: 'CONFIRMED', label: '已确认', count: list.data ? count('CONFIRMED') : undefined, arrow: true },
    { key: 'LOCKED', label: '已锁账', count: list.data ? count('LOCKED') : undefined, arrow: true },
    { key: 'CANCELLED', label: '已作废', count: list.data ? count('CANCELLED') : undefined, sep: true },
  ];
  const chips = <Chips items={chipItems} value={st} onChange={(k) => set({ st: k === 'all' ? undefined : k, id: undefined, page: undefined })} />;

  const filterCount = (scope ? 1 : 0) + (month ? 1 : 0);
  const filterBtn = (boxed?: boolean) => <FilterButton count={filterCount} boxed={boxed} onClick={() => setFilterOpen(true)} />;
  const applied = filterCount > 0 && (
    <div className={s.applied}>
      {scope && <button className={s.achip} onClick={() => set({ scope: undefined })}>{scope === 'supplier' ? '供应商对账' : '客户对账'}<Icon name="x" size={14} /></button>}
      {month && <button className={s.achip} onClick={() => setPeriod({ kind: 'all' })}>{month}<Icon name="x" size={14} /></button>}
    </div>
  );
  const createDialog = createPicker && <SettlementUnitPicker initialScope={scope || (perms.customer ? 'customer' : 'supplier')} month={month || thisMonth()} action="statement" onClose={() => setCreatePicker(false)} />;
  const filterSheet = filterOpen && (
    <FilterSheet
      sheet={ff === 'phone'}
      onClose={() => setFilterOpen(false)}
      value={{ scope: scope || 'all', kind: period.kind }}
      defaults={{ scope: 'all', kind: 'all' }}
      groups={[
        ...(both ? [{ key: 'scope', title: '对账类型', opts: [{ value: 'all', label: '全部' }, { value: 'customer', label: '客户对账' }, { value: 'supplier', label: '供应商对账' }] }] : []),
        { key: 'kind', title: '结算月份', required: true, opts: [{ value: 'all', label: '全部月份' }, { value: 'month', label: '按月' }] },
      ]}
      hint="保存即快照：业务单据之后再变，也不影响已经出的对账单。"
      onApply={(f) => {
        setFilterOpen(false);
        const p: Period = f.kind === 'month' ? (period.kind === 'month' ? period : { kind: 'month', value: thisMonth() }) : { kind: 'all' };
        set({ scope: f.scope !== 'all' ? f.scope : undefined, period: periodParam(p, 'all'), id: undefined, page: undefined });
      }}
    />
  );

  const body: ReactNode = (() => {
    if (list.error && !list.data) return <ErrorState title="对账单加载失败" message={list.error.message} detail={`GET ${list.url}`} onRetry={list.reload} />;
    if (!list.data) return <div className={s.pad}><Skeleton /><Skeleton lines={3} /></div>;
    if (!shown.length) {
      return (
        <Empty icon="receipt" title={q ? `没有符合「${q}」的对账单` : st !== 'all' ? `没有${statementStatus[st].label}的对账单` : '还没有对账单'}>
          对账单在老版「往来结算 › 对账单」里选单位和月份后保存生成；保存即形成快照。
        </Empty>
      );
    }
    return null;
  })();
  const searchBox = (cls?: string) => <SearchBox key={q} className={cls} value={q} placeholder="对账单号 / 往来单位" onSubmit={(v) => set({ q: v || undefined, id: undefined, page: undefined })} />;
  const note = <p className={s.note}>保存即快照，业务单据再变也不影响已出的对账单<br />只有「已锁账」才能生成正式 PDF</p>;
  const actionButton = (item: StatementListItem, action: StatementAction, label: string) => <button key={action} type="button" className={s.actionButton} onClick={(event) => { event.stopPropagation(); setActionTarget({ item, action }); }}>{label}</button>;
  const actions = (item: StatementListItem, includeCancel = false) => <span className={s.actions}>
    {item.status === 'DRAFT' && perms.confirm && actionButton(item, 'confirm', '确认')}
    {item.status === 'DRAFT' && includeCancel && perms.statementWrite && actionButton(item, 'cancel', '作废')}
    {item.status === 'CONFIRMED' && perms.confirm && actionButton(item, 'reopen', '反确认')}
    {item.status === 'CONFIRMED' && perms.lock && actionButton(item, 'lock', '锁账')}
    {item.status === 'LOCKED' && perms.lock && actionButton(item, 'unlock', '解锁')}
    {item.status === 'CANCELLED' && <span>不参与统计</span>}
  </span>;
  const actionDialog = actionTarget && <StatementActionDialog item={actionTarget.item} action={actionTarget.action} onClose={() => setActionTarget(null)} onDone={() => { setActionTarget(null); list.reload(); }} />;
  const exportDialog = exportOpen && <StatementBatchExport items={ordered} onClose={() => setExportOpen(false)} />;
  const bar = (
    <div className={s.bar}>
      <PeriodBar period={period} onPeriod={setPeriod} />
      <span className={s.cnt}>{shown.length} 份{list.loading && list.data ? ' · 更新中…' : ''}</span>
      <button type="button" className={s.sortButton} aria-label={`最近动作，${sortNewest ? '最新在前' : '最早在前'}；点击切换`} onClick={() => setSortNewest((value) => !value)}>最近动作<Icon name="sort" size={14} /></button>
    </div>
  );

  const desktopViews: SidebarView[] = [
    { key: 'all', label: '全部', icon: 'list', count: all.length },
    { key: 'DRAFT', label: '草稿', icon: 'flag', count: count('DRAFT') },
    { key: 'CONFIRMED', label: '已确认', icon: 'check', count: count('CONFIRMED') },
    { key: 'LOCKED', label: '已锁账', icon: 'lock', count: count('LOCKED') },
    { key: 'CANCELLED', label: '已作废', icon: 'trash', count: count('CANCELLED') },
    ...(both ? [
      { key: 'customer', label: '客户对账', icon: 'user', count: all.filter((x) => x.partnerType === 'CUSTOMER').length, separator: true },
      { key: 'supplier', label: '供应商对账', icon: 'team', count: all.filter((x) => x.partnerType === 'SUPPLIER').length },
    ] : []),
  ];
  const desktopActive = scope || st;
  const pickDesktopView = (key: string) => {
    if (key === 'customer' || key === 'supplier') {
      set({ scope: scope === key ? undefined : key, st: undefined, id: undefined, page: undefined });
      return;
    }
    set({ st: key === 'all' ? undefined : key, scope: undefined, id: undefined, page: undefined });
  };
  const desktopAction = (item: StatementListItem, action: StatementAction, label: string, icon: string) => (
    <button key={action} type="button" className={s.desktopIconAction} aria-label={label} title={label} onClick={(event) => { event.stopPropagation(); setActionTarget({ item, action }); }}><Icon name={icon} size={14} /></button>
  );
  const desktopNextActions = (item: StatementListItem) => <span className={s.desktopNextActions}>
    {item.status === 'DRAFT' && perms.confirm && desktopAction(item, 'confirm', '确认', 'check')}
    {item.status === 'DRAFT' && perms.statementWrite && desktopAction(item, 'cancel', '作废', 'trash')}
    {item.status === 'CONFIRMED' && perms.confirm && desktopAction(item, 'reopen', '反确认', 'return')}
    {item.status === 'CONFIRMED' && perms.lock && desktopAction(item, 'lock', '锁账', 'lock')}
    {item.status === 'LOCKED' && perms.lock && desktopAction(item, 'unlock', '解锁', 'unlock')}
    {item.status === 'CANCELLED' && <span title="不参与统计">—</span>}
  </span>;

  /* ------------------------------ 手机 ------------------------------ */
  if (ff === 'phone') {
    return (
      <div className={s.phone}>
        <StlHeader phone active="statement" actions={<><IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />{perms.statementWrite && <IconButton name="plus" label="生成对账单" onClick={() => {
          const createMonth = period.kind === 'month' ? period.value : ordered[0]?.settlementMonth || thisMonth();
          nav(`${perms.customer ? '/settlements/customers' : '/settlements/suppliers'}?period=${encodeURIComponent(createMonth)}`);
        }} />}</>} />
        {searching && <div className={s.psearch}>{searchBox()}</div>}
        <div className={s.chipbar}><div className={s.chipsBox}>{chips}</div>{filterBtn()}</div>
        {applied}
        <div className={s.pbody}>
          {bar}
          {note}
          {body || ordered.map((x) => <StatementCard key={x.id} x={x} to={`/settlements/statements/${x.id}`} actions={actions(x)} />)}
          <div className={s.end}>{!body && '没有更多了'}</div>
        </div>
        {filterSheet}{createDialog}
        {actionDialog}
      </div>
    );
  }

  /* ------------------------------ iPad：T7 全宽台账 ------------------------------ */
  if (ff === 'tablet') {
    const pages = Math.max(1, Math.ceil(shown.length / PAGE));
    const cur = Math.min(page, pages);
    const rows = ordered.slice((cur - 1) * PAGE, cur * PAGE);
    const cols: Col<StatementListItem>[] = [
      ...statementCols.filter((col) => !['i', 'pdf'].includes(col.key)),
      { key: 'next', title: '下一步', w: 170, render: (x) => actions(x) },
    ];
    return (
      <div className={`${s.page} ${s.tabletPage}`}>
        <StlHeader phone={false} active="statement" actions={<>{searchBox(s.tabletSearch)}<Button icon="down" onClick={() => setExportOpen(true)}>批量导出 PDF</Button>{perms.statementWrite && <Button icon="plus" onClick={() => setCreatePicker(true)}>生成对账单</Button>}</>} />
        <div className={s.tabletBar}>
          <div className={s.chipsBox}>{chips}</div>
          <PeriodBar period={period} onPeriod={setPeriod} />
          {filterBtn(true)}
        </div>
        {applied}
        <div className={s.tabletNote}>{note}<span>{shown.length} 份 · 草稿 {count('DRAFT')} · 已确认 {count('CONFIRMED')} · 已锁账 {count('LOCKED')} · 已作废 {count('CANCELLED')}</span></div>
        {body ? <div className={s.dbody}>{body}</div> : <Table cols={cols} rows={rows} rowKey={(x) => x.id} onRow={(x) => nav(`/settlements/statements/${x.id}`)} dim={(x) => x.status === 'CANCELLED'} />}
        {!body && <footer className={s.pager}>
          <span>第 {(cur - 1) * PAGE + 1}–{(cur - 1) * PAGE + rows.length} 份 / 共 {shown.length} 份</span>
          <span className={s.sp} />
          <Button size="sm" disabled={cur <= 1} onClick={() => set({ page: cur - 1 > 1 ? String(cur - 1) : undefined })}>上一页</Button>
          <span className="tnum">{cur} / {pages}</span>
          <Button size="sm" disabled={cur >= pages} onClick={() => set({ page: String(cur + 1) })}>下一页</Button>
        </footer>}
        {filterSheet}{createDialog}
        {actionDialog}
        {exportDialog}
      </div>
    );
  }

  /* ------------------------------ 桌面 ------------------------------ */
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const cur = Math.min(page, pages);
  const rows = ordered.slice((cur - 1) * PAGE, cur * PAGE);
  const desktopCols: Col<StatementListItem>[] = [
    ...statementCols.filter((col) => !['i', 'pdf'].includes(col.key)).map((col) => ({ ...col, w: col.key === 'no' ? 155 : col.key === 'p' ? 140 : col.key === 'm' ? 80 : col.key === 'st' ? 70 : col.key === 'b' || col.key === 'c' || col.key === 'o' ? 115 : 120 })),
    {
      key: 'ops', title: '操作', w: 125, fixRight: true,
      render: (x) => <span className={s.desktopOps}>
        <Link className={s.desktopDetail} aria-label="查看" title="查看" to={`/settlements/statements/${x.id}`} onClick={(event) => event.stopPropagation()}><Icon name="eye" size={14} /></Link>
        <PdfLink url={x.pdfFileUrl} compact />
        {desktopNextActions(x)}
      </span>,
    },
  ];
  return (
    <div className={`${s.page} ${s.desktopPage}`}>
      <StlHeader phone={false} active="statement" />
      <div className={s.desktopHeading}>
        <div className={s.desktopTitleGroup}>
          <h2>对账单</h2>
          <p>保存即形成快照。之后业务单据再变动，不影响已出的对账单 <span>共 {all.length} 份 · 草稿 {count('DRAFT')} · 已确认 {count('CONFIRMED')} · 已锁账 {count('LOCKED')} · 已作废 {count('CANCELLED')}</span></p>
        </div>
        <div className={s.desktopHeadingActions}>
          <Button icon="down" onClick={() => setExportOpen(true)}>批量导出 PDF</Button>
          {perms.statementWrite && <Button icon="plus" onClick={() => setCreatePicker(true)}>生成对账单</Button>}
        </div>
      </div>
      <div className={s.desktopWorkspace}>
        <ViewSidebar title="对账单状态" views={desktopViews} active={desktopActive} onPick={pickDesktopView} />
        <section className={s.desktopContent}>
          <div className={s.desktopToolbar}>
            {searchBox(s.dsearch)}
            <PeriodBar period={period} onPeriod={setPeriod} />
            {filterBtn(true)}
            <span className={s.sp} />
            <Button icon="ref" onClick={list.reload}>刷新</Button>
          </div>
          {applied}
          {body ? <div className={s.dbody}>{body}</div> : (
            <div className={s.desktopTable}>
              <Table cols={desktopCols} rows={rows} rowKey={(x) => x.id} onRow={(x) => nav(`/settlements/statements/${x.id}`)} dim={(x) => x.status === 'CANCELLED'} />
            </div>
          )}
          {!body && (
            <footer className={s.desktopPager}>
              <span>第 {(cur - 1) * PAGE + 1}–{(cur - 1) * PAGE + rows.length} 份 / 共 {shown.length} 份 · 只有「已锁账」才能生成正式 PDF</span>
              <span className={s.sp} />
              <Button size="sm" disabled={cur <= 1} onClick={() => set({ page: cur - 1 > 1 ? String(cur - 1) : undefined })}>‹</Button>
              <span className="tnum">{cur} / {pages}</span>
              <Button size="sm" disabled={cur >= pages} onClick={() => set({ page: String(cur + 1) })}>›</Button>
            </footer>
          )}
        </section>
      </div>
      {filterSheet}{createDialog}
      {actionDialog}
      {exportDialog}
    </div>
  );
}
